/**
 * Legacy task list URLs (`/t/li/:type/:id`, old `view` + `id` query pairs).
 * Scheduled for removal once external links are migrated.
 */
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { BUILTIN_TASK_VIEW_KEYS, TaskListType } from "@web/features/Tasks/TaskViewKeys";
import { TASK_VIEW_SCOPE_QUERY } from "@web/features/Tasks/Views/taskViewScopeQuery";

export const LEGACY_TASK_LIST_TYPE_MAP: Record<string, TaskListType> = {
  td: TaskListType.TYPE_TODAY,
  atd: TaskListType.TYPE_ALL_TODAY,
  po: TaskListType.TYPE_PER_PROPERTY,
  a: TaskListType.TYPE_ALL,
  atud: TaskListType.TYPE_ALL_UNTIL_TODAY,
  oto: TaskListType.TYPE_OPEN_UNTIL_TODAY,
  asg: TaskListType.TYPE_ASSIGNED_TO_ME,
  ro: TaskListType.TYPE_ROUTE,
  wr: TaskListType.TYPE_WALK_ROUTE,
  uasg: TaskListType.TYPE_UNASSIGNED,
  rd: TaskListType.TYPE_READY,
  fn: TaskListType.TYPE_FINISHED,
  ug: TaskListType.TYPE_USER_GROUP,
  u: TaskListType.TYPE_USER,
};

const LEGACY_VIEW_ALIASES: Record<string, string> = {
  "all-today": BUILTIN_TASK_VIEW_KEYS.ALL_TODAY,
  "all-until-today": BUILTIN_TASK_VIEW_KEYS.ALL_UNTIL_TODAY,
  "open-until-today": BUILTIN_TASK_VIEW_KEYS.MY_UNTIL_TODAY,
  "assigned-to-me": BUILTIN_TASK_VIEW_KEYS.ASSIGNED_TO_ME,
  "walk-route": BUILTIN_TASK_VIEW_KEYS.WALK_ROUTE,
  "property": BUILTIN_TASK_VIEW_KEYS.ALL,
  "user": BUILTIN_TASK_VIEW_KEYS.ALL,
  "user-group": BUILTIN_TASK_VIEW_KEYS.ALL,
};

export function normalizeLegacyTaskViewQuery(view: string): string {
  return LEGACY_VIEW_ALIASES[view] ?? view;
}

export function legacyTaskListTypeToQuery(type: TaskListType, id?: string): Record<string, string> {
  switch (type) {
    case TaskListType.TYPE_PER_PROPERTY:
      return id
        ? { view: BUILTIN_TASK_VIEW_KEYS.ALL, [TASK_VIEW_SCOPE_QUERY.property]: id }
        : { view: BUILTIN_TASK_VIEW_KEYS.ALL, [TASK_VIEW_SCOPE_QUERY.propertyBrowse]: "1" };
    case TaskListType.TYPE_USER:
      return { view: BUILTIN_TASK_VIEW_KEYS.ALL, [TASK_VIEW_SCOPE_QUERY.user]: id ?? "" };
    case TaskListType.TYPE_USER_GROUP:
      return { view: BUILTIN_TASK_VIEW_KEYS.ALL, [TASK_VIEW_SCOPE_QUERY.userGroup]: id ?? "" };
    case TaskListType.TYPE_WALK_ROUTE:
      return id
        ? { view: BUILTIN_TASK_VIEW_KEYS.WALK_ROUTE, [TASK_VIEW_SCOPE_QUERY.propertyRoute]: id }
        : { view: BUILTIN_TASK_VIEW_KEYS.WALK_ROUTE };
    default:
      return { view: type };
  }
}

/**
 * Without a view the result has none either: the route then opens the user's default view, and a
 * legacy `id` means nothing without its list type.
 */
export function migrateLegacyViewAndIdQuery(query: Record<string, unknown>): Record<string, string> {
  const rawView = typeof query.view === "string" ? query.view : null;
  const id = typeof query.id === "string" ? query.id : undefined;
  const next: Record<string, string> = {};

  for (const [key, value] of Object.entries(query)) {
    if (key === "view" || key === "id") {
      continue;
    }
    if (typeof value === "string") {
      next[key] = value;
    }
  }

  if (rawView === null) {
    return next;
  }
  const view = normalizeLegacyTaskViewQuery(rawView);

  if (rawView === TaskListType.TYPE_PER_PROPERTY) {
    Object.assign(next, legacyTaskListTypeToQuery(TaskListType.TYPE_PER_PROPERTY, id));
    return next;
  }
  if (rawView === TaskListType.TYPE_USER && id) {
    Object.assign(next, legacyTaskListTypeToQuery(TaskListType.TYPE_USER, id));
    return next;
  }
  if (rawView === TaskListType.TYPE_USER_GROUP && id) {
    Object.assign(next, legacyTaskListTypeToQuery(TaskListType.TYPE_USER_GROUP, id));
    return next;
  }
  if (rawView === TaskListType.TYPE_WALK_ROUTE) {
    const rawRoute = query[TASK_VIEW_SCOPE_QUERY.propertyRoute];
    const existingRoute = typeof rawRoute === "string" ? rawRoute : undefined;
    const routeId = id ?? existingRoute;
    Object.assign(next, legacyTaskListTypeToQuery(TaskListType.TYPE_WALK_ROUTE, routeId));
    return next;
  }

  next.view = view;
  return next;
}

export function isLegacyTaskListType(value: unknown): value is TaskListType {
  return typeof value === "string" && (Object.values(TaskListType) as string[]).includes(value);
}

export function legacyTaskListRedirect(to: RouteLocationNormalized): RouteLocationRaw {
  const rawType = String(to.params.type ?? "");
  const mapped = LEGACY_TASK_LIST_TYPE_MAP[rawType] ?? (isLegacyTaskListType(rawType) ? rawType : null);
  let id = typeof to.params.id === "string" ? to.params.id : undefined;
  let taskId = typeof to.params.taskId === "string" ? to.params.taskId : undefined;
  if (id && !taskId && (id === "new" || /^t\d+$/.test(id))) {
    taskId = id;
    id = undefined;
  }
  const params: Record<string, string> = {};
  if (taskId === "new") {
    params.taskId = "new";
  } else if (taskId && /^t\d+$/.test(taskId)) {
    params.taskId = taskId.slice(1);
  }

  const queryParts = mapped
    ? legacyTaskListTypeToQuery(mapped, id)
    : { view: normalizeLegacyTaskViewQuery(rawType) };

  const query: Record<string, string> = {
    ...(to.query as Record<string, string>),
    ...queryParts,
  };
  delete query.id;

  return { name: "task-list", params, query };
}
